import React from "react";
import { useState, useEffect } from "react";
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Avatar, Typography, Box, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { ReactComponent as Edit } from "../../../../assets/images/edit.svg";
import { useNavigate } from "react-router-dom";
import male from "../../../../assets/images/male.png";
import color from "../../../../constants/color";
import styles from "./style";
import CustomPagination from "../../../../components/CustomPagination";
import Modal from "../../../../components/Modal";
import AuthService from "../../../../api/AuthService";
import { formatDate } from "../../../../utils/DateFormat";

const EmployeeList = ({ data, handleDeleteEmployee, page, setPage, total, open, setOpen, permission, role, employeeId, selectedStatus }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [value, setValue] = useState();
  const [inactive, seInactive] = useState(false);
  const [employeePermission, setEmployeePermission] = useState(null);
  const navigate = useNavigate();
  
  const userRole = role || localStorage.getItem("role");
  const currentEmployeeId = employeeId || localStorage.getItem("employeeId");
  const isInactiveFilter = selectedStatus === "inactive";
  
  // Fetch permissions from API only for Employee role
  useEffect(() => {
    const fetchPermissions = async () => {
      // If permission is passed as prop, use it
      if (permission) {
        setEmployeePermission(permission);
        return;
      }
      
      // For Employee role, fetch from API
      if ((userRole === "Employee" || userRole === "employee") && currentEmployeeId) {
        try {
          const permissionResult = await AuthService.getEmployeePermission(currentEmployeeId);
          if (permissionResult?.success && permissionResult?.data) {
            // API returns permissions directly in data object
            const permissions = permissionResult?.data;
            setEmployeePermission(permissions);
          }
        } catch (error) {
          console.error("Error fetching permissions:", error);
        }
      } else {
        // For Admin/SuperAdmin, use localStorage
        const permissionObj = localStorage.getItem("permission");
        setEmployeePermission(permissionObj ? JSON.parse(permissionObj) : null);
      }
    };
    
    fetchPermissions();
    
    // Listen for permission updates
    const handlePermissionUpdate = () => {
      fetchPermissions();
    };
    
    window.addEventListener("permissionUpdated", handlePermissionUpdate);
    return () => {
      window.removeEventListener("permissionUpdated", handlePermissionUpdate);
    };
  }, [permission, userRole, currentEmployeeId]);
  
  // Determine if buttons should be shown
  const isEmployee = userRole === "Employee" || userRole === "employee";
  const isAdmin = userRole === "Admin" || userRole === "SuperAdmin";
  
  const hasViewPermission = employeePermission?.employee?.isView === true;
  const hasEditPermission = employeePermission?.employee?.isEdit === true;
  const hasDeletePermission = employeePermission?.employee?.isDelete === true;
  
  // Don't render until permissions are loaded
  if (employeePermission === null && (isEmployee || !isAdmin)) {
    return null;
  }
  
  // For employee role with only view permission (no edit), hide all action buttons
  const canShowActions = isAdmin || (isEmployee && hasEditPermission) || (!isEmployee && (hasEditPermission || hasDeletePermission));
  
  // For employee with edit permission, can edit all employees (like admin)
  const canEditEmployee = (item) => {
    if (isAdmin) return true;
    if (isEmployee) {
      // Employee with edit permission can edit all employees
      return hasEditPermission;
    }
    // Non-employee users (if any)
    return hasEditPermission;
  };
  
  const canDeleteEmployee = (item) => {
    // If employee has edit permission, they can delete (like admin)
    if (isEmployee && hasEditPermission) return true;
    if (isAdmin) return true;
    return hasDeletePermission;
  };
  
  const canBlockEmployee = (item) => {
    // If employee has edit permission, they can block (like admin)
    if (isEmployee && hasEditPermission) return true;
    if (isAdmin) return true;
    return hasEditPermission;
  };

  const deleteData = (item) => {
    setValue(item);
  }

  const handleNavigate = (from, item) => {
    if (from === "View") {
      navigate("/dashboard/manageStaff/viewEmployee", { state: { item } });
    }
    else {
      navigate("/dashboard/manageStaff/editProfile", { state: { item } });
    }
  }
  return (
    <>
      <TableContainer sx={styles.noShadow}>
        <Table  >
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHead}>Name</TableCell>
              <TableCell sx={styles.tableHead}>Department</TableCell>
              <TableCell sx={styles.tableHead}>Role</TableCell>
              <TableCell sx={styles.tableHead}>Mobile</TableCell>
              {isInactiveFilter && <TableCell sx={styles.tableHead}>Inactive Period</TableCell>}
              <TableCell sx={styles.tableHead}>Joining Date</TableCell>
              <TableCell sx={styles.tableHead}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isInactiveFilter ? 7 : 6} align="center">
                  <Typography variant="h6">No Data</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, index) => {
                return (<TableRow onClick={() => handleNavigate("View", item)} key={index} sx={{ bgcolor: item.isActive ? "white" : color.lightGray, cursor: "pointer" }}>
                  <TableCell sx={{
                    ...styles.profileData,
                    color: item.isActive ? color.text : color.lightGray,
                  }}>
                    <Box style={{ color: item.isActive ? color.text : color.lightGray, textDecoration: "none", display: "flex", flexDirection: "row" }}>
                      <Avatar src={item?.personalInfo?.image ? item?.personalInfo?.image : male} sx={{ mr: 2 }} />
                      <Box>
                        <Typography sx={{ ...styles.name, color: item.isActive ? color.text : color.lightText }}>{item?.personalInfo?.firstName} {item?.personalInfo?.surName}</Typography>
                        <Typography sx={styles.mail}>{item?.personalInfo?.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>{item?.personalInfo?.department}</TableCell>
                  <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>{item?.personalInfo?.jobPosition}</TableCell>
                  <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>+{item?.personalInfo?.mobile}</TableCell>
                  {isInactiveFilter && (
                    <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>{item?.inactiveDays || "-"}</TableCell>
                  )}
                  <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>{item?.experience?.joiningDate && formatDate(item.experience.joiningDate)}</TableCell>
                  <TableCell sx={{ ...styles.tableData, color: item.isActive ? color.text : color.lightText }}>
                    {canShowActions && (
                      <Box sx={styles.tableActiondata}>
                        {canEditEmployee(item) && (
                          <Edit onClick={(e) => { e.stopPropagation(); if (!item.isActive) { return } handleNavigate("Edit", item); }} sx={{ cursor: item.isActive ? "pointer" : 'not-allowed !important' }} />
                        )}
                        {canBlockEmployee(item) && (
                          <Tooltip title={item.isActive ? "Block" : "Blocked"}>
                            <BlockOutlinedIcon sx={{ color: item.isActive ? "#ff0000" : color.lightText, ml: 2 }} fontSize="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item.isActive) {
                                  deleteData(item?._id), seInactive(true);
                                }
                              }} />
                          </Tooltip>
                        )}
                        {canDeleteEmployee(item) && (
                          <DeleteOutlineIcon sx={{ color: item.isActive ? "#ff0000" : color.lightText, ml: 2 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteData(item?._id), setOpen(true);
                            }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>);
              })
            )}
          </TableBody>

        </Table>
        {data &&
          <CustomPagination
            pageCount={total}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalItems={data.length}
          />}

      </TableContainer>


      <Modal open={open} setOpen={setOpen} deletetText={"Delete"} cancelText={"Cancel"} title={"Delete Employee"} handleDelete={() => { handleDeleteEmployee(value, "delete") }}>
        <Box>
          <Typography>Are you sure you want to Delete?</Typography>

        </Box>
      </Modal>

      <Modal open={inactive} setOpen={seInactive} deletetText={"Inactive"} cancelText={"Cancel"} title={"Inactive Employee"} handleDelete={() => { handleDeleteEmployee(value, "inactive"); seInactive(false) }}>
        <Box>
          <Typography>Are you sure you want to Inactive Employee</Typography>
        </Box>
      </Modal>
    </>
  );
};
export default EmployeeList;
